<script>
  import EmailSignaturePreview from './EmailSignaturePreview.svelte'

  const l = window.localStorage
  const isDebug = new URLSearchParams(window.location.search).get('debug') === 'true'
  const log = (...args) => isDebug && console.log(...args)
  const error = (...args) => isDebug && console.error(...args)

  let name = l.getItem('name') || 'James Boyce'
  let role = l.getItem('role') || 'CEO & Founder'
  let mobile = l.getItem('mobile') || '0444 000 111'
  let email = l.getItem('email') || 'james'
  let specialEvent = l.getItem('specialEvent') || '0'
  let bannerPlacement = l.getItem('bannerPlacement') || 'above'
  let copied = false
  let timeout
  let renderedSignature

  const showMessage = () => {
    clearTimeout(timeout)
    timeout = setTimeout(() => {
      copied = false
    }, 2000)
  }

  const onPreviewRender = (event) => {
    // Remove cache busting parameter from the logo.gif URL when copying
    renderedSignature = event.detail.replace(/logo\.gif\?v=\d+/g, 'logo.gif')
    log('Preview rendered, signature HTML:', renderedSignature)
  }

  const onCopyClick = async () => {
    log('Copy button clicked')
    log('Current renderedSignature:', renderedSignature)

    try {
      if (navigator.clipboard?.write) {
        log('Trying modern Clipboard API...')

        const htmlBlob = new Blob([renderedSignature], { type: 'text/html' })
        const textBlob = new Blob([renderedSignature], { type: 'text/plain' })

        log('Created HTML blob:', htmlBlob)
        log('Created text blob:', textBlob)

        try {
          const data = [new ClipboardItem({
            'text/html': htmlBlob,
            'text/plain': textBlob
          })]
          await navigator.clipboard.write(data)
          log('Modern clipboard write successful (both formats)')
        } catch (err) {
          log('Failed to write both formats, trying HTML only:', err)
          const data = [new ClipboardItem({ 'text/html': htmlBlob })]
          await navigator.clipboard.write(data)
          log('Modern clipboard write successful (HTML only)')
        }

        copied = true
        showMessage()
        return
      }

      log('Clipboard API not available, using fallback')
      fallbackCopy()
    } catch (err) {
      error('Clipboard API failed:', err)
      log('Error details:', err.message, err.stack)

      if (navigator.clipboard?.writeText) {
        try {
          log('Trying writeText method...')
          await navigator.clipboard.writeText(renderedSignature)
          log('writeText successful')
          copied = true
          showMessage()
          return
        } catch (textErr) {
          error('writeText also failed:', textErr)
        }
      }

      fallbackCopy()
    }
  }

  const fallbackCopy = () => {
    log('Using fallback copy method...')
    try {
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = renderedSignature
      tempDiv.style.position = 'absolute'
      tempDiv.style.left = '-9999px'
      document.body.appendChild(tempDiv)
      log('Created temp div with HTML:', tempDiv.innerHTML)

      const range = document.createRange()
      range.selectNodeContents(tempDiv)
      const selection = window.getSelection()
      selection.removeAllRanges()
      selection.addRange(range)
      log('Selection range:', range.toString())

      const success = document.execCommand('copy')
      log('execCommand copy result:', success)

      selection.removeAllRanges()
      document.body.removeChild(tempDiv)

      if (success) {
        log('Fallback copy successful')
        copied = true
        showMessage()
      } else {
        error('Fallback copy failed - execCommand returned false')
        copied = false
      }
    } catch (err) {
      error('Fallback copy error:', err)
      log('Error details:', err.message, err.stack)
      copied = false
    }
  }

  $: {
    if (name !== l.getItem('name')) l.setItem('name', name.trim())
    if (role !== l.getItem('role')) l.setItem('role', role.trim())
    if (mobile !== l.getItem('mobile')) l.setItem('mobile', mobile.trim())
    if (email !== l.getItem('email')) l.setItem('email', email.trim())
    if (specialEvent !== l.getItem('specialEvent')) l.setItem('specialEvent', specialEvent.trim())
    if (bannerPlacement !== l.getItem('bannerPlacement')) l.setItem('bannerPlacement', bannerPlacement.trim())
  }
</script>

<div class="flex form">
  <div class="input">
    <p class="label">Name</p>
    <p><input bind:value={name} /></p>
  </div>
  <div class="input">
    <p class="label">Role</p>
    <p><input bind:value={role} /></p>
  </div>
  <div class="input">
    <p class="label">Mobile</p>
    <p><input bind:value={mobile} /></p>
  </div>
  <div class="input">
    <p class="label">Email</p>
    <p><input bind:value={email} />@grumpysailor.com</p>
  </div>
  <div class="input">
    <p class="label">Banner Placement</p>
    <div class="radio-group">
      <label>
        <input type="radio" bind:group={bannerPlacement} value="above" />
        Above Details
      </label>
      <label>
        <input type="radio" bind:group={bannerPlacement} value="below" />
        Below Details
      </label>
    </div>
  </div>
  <div class="input">
    <label for="specialEvent" class="label">Special Events</label>
    <select bind:value={specialEvent}>
      <option value="0">None</option>
      <option value="1">International Women's Day</option>
    </select>
  </div>

  <div class="actions">
    <button class="btn-grad" on:click={onCopyClick}
      >Copy Email Signature</button
    >
    {#if copied}
      <span class="message">Rich text copied to your clipboard!</span>
    {/if}
  </div>
</div>

<EmailSignaturePreview
  {name}
  {email}
  {mobile}
  {role}
  {specialEvent}
  {bannerPlacement}
  on:render={onPreviewRender}
/>

<style>
  .flex {
    display: flex;
    flex-direction: column;
  }

  .form {
    font-size: 0.9rem;
  }

  p {
    margin: 0;
  }

  input {
    border: 0;
    border-bottom: 1px solid #444;
  }

  button {
    padding: 3rem;
    font-size: 1rem;
    font-weight: bold;
    cursor: pointer;
    display: inline-block;
  }

  .input {
    margin-bottom: 0.5rem;
  }

  .label {
    font-size: 0.7rem;
    text-transform: uppercase;
    font-weight: 600;
    color: #999;
  }

  .input > select {
    padding: 0.4rem;
    border: 1px solid #999;
    border-radius: 4px;
    background-color: #fff;
    font-size: 0.8rem;
    margin-top: 0.25rem;
  }

  .btn-grad {
    background-image: linear-gradient(
      to right,
      #da22ff 0%,
      #9733ee 51%,
      #da22ff 100%
    );
  }

  .btn-grad {
    padding: 1rem 1.25rem;
    text-align: center;
    text-transform: uppercase;
    transition: 0.5s;
    background-size: 200% auto;
    color: white;
    box-shadow: 0 0 20px #eee;
    border-radius: 10px;
    display: inline-block;
    margin-bottom: 0;
    font-size: 0.9rem;
    font-weight: 600;
  }

  .btn-grad:hover {
    background-position: right center;
    color: #fff;
    text-decoration: none;
  }

  .message {
    background-color: rgb(78, 233, 174);
    border-radius: 0;
    padding: 1rem 2rem;
    display: inline-block;
    margin-left: 1rem;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    border: 1px solid #444;
    font-size: 0.9rem;
  }

  .actions {
    display: flex;
    flex-direction: row;
    justify-items: center;
    align-items: center;
  }

  .radio-group {
    display: flex;
    gap: 1rem;
    margin-top: 0.25rem;
  }

  .radio-group label {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.8rem;
    cursor: pointer;
  }

  .radio-group input[type="radio"] {
    margin: 0;
    cursor: pointer;
  }
</style>
